import { useCallback } from 'react';
import { ApiError } from '../../api/client';
import type { ComponentManagerState } from './types';
import { getApiBaseUrl } from './constants';

interface UseSaveEditHandlerProps {
  pageId: string;
  state: ComponentManagerState;
  /** Called after a component is successfully saved, so parent can refresh page metadata */
  onComponentSaved?: () => void;
  /** Called with updated components array so parent state stays in sync */
  onComponentsChanged?: (components: import('../../schemas/validation').Component[]) => void;
}

export const useSaveEditHandler = ({
  pageId,
  state,
  onComponentSaved,
  onComponentsChanged,
}: UseSaveEditHandlerProps) => {
  const handleSaveEdit = useCallback(
    async (componentId: string, updatedData: any) => {
      try {
        state.setIsSavingEdit(true);

        console.log('Saving component edit:', { componentId, updatedData, pageId });

        const currentComponent = state.components.find(c => c.id === componentId);
        if (!currentComponent) {
          throw new Error('Component not found');
        }

        if (currentComponent.type === 'features') {
          logFeaturesEdit(updatedData);
        }

        try {
          const requestData = {
            componentId: componentId,
            updates: { data: updatedData },
          };

          console.log('Sending backend-compliant format:', requestData);
          console.log('Data breakdown:', {
            componentId: requestData.componentId,
            'updates.data.siteName': requestData.updates.data.siteName,
            'updates.data.links': requestData.updates.data.links,
            'updates.data.alignment': requestData.updates.data.alignment,
            'updates.data.sticky': requestData.updates.data.sticky,
          });
          console.log('Full request details:', {
            url: `${getApiBaseUrl()}/pages/${pageId}/components/${componentId}`,
            method: 'PUT',
            data: requestData,
          });

          const response = await fetch(
            `${getApiBaseUrl()}/pages/${pageId}/components/${componentId}`,
            {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
              },
              body: JSON.stringify(requestData),
            }
          );

          if (!response.ok) {
            const errorText = await response.text();
            console.error('API Error Response:', {
              status: response.status,
              statusText: response.statusText,
              body: errorText,
            });

            let errorData;
            try {
              errorData = JSON.parse(errorText);
              console.error('Parsed error data:', errorData);
              if (errorData.errors) console.error('Validation errors:', errorData.errors);
              if (errorData.details) console.error('Error details:', errorData.details);
            } catch {
              console.error('Could not parse error response as JSON');
              errorData = { message: errorText };
            }

            throw new ApiError(
              errorData.message || `HTTP ${response.status}: ${response.statusText}`,
              response.status,
              errorData
            );
          }

          const result = await response.json();
          console.log('Component update SUCCESS:', result);

          const updated = state.components.map(c => (c.id === componentId ? result.component : c));
          state.setComponents(updated);
          onComponentsChanged?.(updated);
          state.setIsEditModalOpen(false);
          state.setEditingComponent(null);

          // Notify parent so page metadata (badge, timestamps) can refresh
          onComponentSaved?.();
        } catch (apiError) {
          console.warn('API update failed, saving locally:', apiError);

          if (apiError instanceof ApiError) {
            console.error('API Error Details:', {
              status: apiError.status,
              message: apiError.message,
              data: apiError.data,
              url: `${getApiBaseUrl()}/pages/${pageId}/components/${componentId}`,
              method: 'PUT',
            });
            if (apiError.data) {
              console.error('Raw API error response:', apiError.data);
            }
          }

          const updatedComponent = state.components.find(c => c.id === componentId);
          if (updatedComponent) {
            const newComponent = {
              ...updatedComponent,
              data: updatedData,
              updatedAt: new Date().toISOString(),
            };

            const updated = state.components.map(c => (c.id === componentId ? newComponent : c));
            state.setComponents(updated);
            onComponentsChanged?.(updated);
            state.setIsEditModalOpen(false);
            state.setEditingComponent(null);

            console.log('Component updated locally:', newComponent);

            const isNetworkError =
              apiError instanceof ApiError && (apiError.status === 404 || apiError.status >= 500);
            const errorDetails =
              apiError instanceof ApiError
                ? `Status: ${apiError.status}, Message: ${apiError.message}`
                : 'Unknown error';

            const message = isNetworkError
              ? `API endpoint issue (${errorDetails}). Changes saved locally!`
              : `API Error: ${errorDetails}. Changes saved locally!`;

            alert(
              message +
                '\n\nNote: Check the browser console for more details. The backend component routes may need to be properly mounted under /pages.'
            );
          } else {
            throw apiError;
          }
        }
      } catch (err) {
        console.error('Failed to save component changes:', err);

        if (err instanceof ApiError) {
          const errorDetails = [
            `Status: ${err.status}`,
            `Message: ${err.message}`,
            err.data ? `Details: ${JSON.stringify(err.data, null, 2)}` : '',
          ]
            .filter(Boolean)
            .join('\n');

          console.error('API Error Details:', errorDetails);
          state.setError(`Failed to save changes: ${err.message} (Status: ${err.status})`);
        } else {
          console.error('Unknown error:', err);
          state.setError('Failed to save component changes. Please try again.');
        }
      } finally {
        state.setIsSavingEdit(false);
      }
    },
    [pageId, state, onComponentSaved, onComponentsChanged]
  );

  return { handleSaveEdit };
};

function logFeaturesEdit(updatedData: any) {
  console.log('🖼️ ComponentManager: Saving features component:', {
    displayMode: updatedData.displayMode,
    featuresCount: updatedData.features?.length,
    features: updatedData.features?.map((f: any, idx: number) => ({
      index: idx,
      title: f.title,
      hasImageUrl: !!f.imageUrl,
      hasCloudfrontUrl: !!f.cloudfrontUrl,
      hasMediaId: !!f.mediaId,
      hasS3Key: !!f.s3Key,
      imageUrl: f.imageUrl,
      cloudfrontUrl: f.cloudfrontUrl,
      mediaId: f.mediaId,
      s3Key: f.s3Key,
      imageAlt: f.imageAlt,
    })),
  });
}
